/* @refresh reset */
import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import ToastAlert from "./ToastAlert.jsx"

/**
 * @param {{
 *   show?: boolean,
 *   type?: "info" | "warning" | "error" | "success",
 *   title?: string,
 *   message?: string,
 *   onClose?: () => void,
 *   autoDismissMs?: number | null,
 * }} props
 */
export default function Toast({
    show = false,
    type = "error",
    title = "",
    message = "",
    onClose = () => {},
    autoDismissMs = null,
}) {
    const [isVisible, setIsVisible] = useState(show)
    const [isExiting, setIsExiting] = useState(false)
    const [isEntering, setIsEntering] = useState(show)
    const EXIT_DURATION_MS = 220
    const portalRoot = typeof document === "undefined" ? null : document.body

    useEffect(() => {
        if (show) {
            setIsVisible(true)
            setIsExiting(false)
            setIsEntering(true)
            const rafId = window.requestAnimationFrame(() => {
                setIsEntering(false)
            })
            return () => window.cancelAnimationFrame(rafId)
        }
        if (isEntering) {
            setIsEntering(false)
        }
        if (!isVisible) {
            return
        }
        setIsExiting(true)
        const timeout = window.setTimeout(() => {
            setIsVisible(false)
            setIsExiting(false)
        }, EXIT_DURATION_MS)
        return () => window.clearTimeout(timeout)
    }, [show])

    useEffect(() => {
        if (!show || autoDismissMs == null) {
            return
        }
        const timeout = window.setTimeout(() => {
            handleClose()
        }, autoDismissMs)
        return () => window.clearTimeout(timeout)
    }, [show, autoDismissMs])

    if (!isVisible || !message) {
        return null
    }

    function handleClose() {
        if (isExiting) {
            return
        }
        setIsExiting(true)
        window.setTimeout(() => {
            setIsVisible(false)
            setIsExiting(false)
            onClose()
        }, EXIT_DURATION_MS)
    }

    const content = (
        <div
            className="position-fixed bottom-0 end-0 m-4"
            style={{
                zIndex: 2000,
                transform: isExiting || isEntering ?
                    "translateX(120%)" :
                    "translateX(0)",
                opacity: isExiting || isEntering ? 0 : 1,
                transition: `transform ${EXIT_DURATION_MS}ms ease, opacity ${EXIT_DURATION_MS}ms ease`,
            }}
        >
            <div style={{ minWidth: 0, maxWidth: "min(420px, calc(100vw - 2rem))", width: "100%" }}>
                <ToastAlert
                    type={type}
                    title={title}
                    message={message}
                    onClose={handleClose}
                />
            </div>
        </div>
    )

    return portalRoot ? createPortal(content, portalRoot) : content
}
